/**
 * scope-secici.tsx
 *
 * NE İŞE YARAR : Üst çubuktaki kurum seçici — hangi yurtta çalışıldığını gösterir ve seçtirir
 * KAYNAK SPEC  : 15-arayuz-standardi.md §5.1 (üst çubuk) · §5.3 (scope seçici)
 *                §4.1 (renk) · §13 (erişilebilirlik — form etiketleri)
 * BAĞLI OLDUĞU : src/kabuk/scope-baglami.tsx · @mantine/core
 * KULLANAN     : src/kabuk/kabuk.tsx
 * DİKKAT       : 1) GÖSTERGE **GİZLENEMEZ VE KÜÇÜLTÜLEMEZ** (`15` §5.3 — bağlayıcı).
 *                   *"Yanlış yurtta işlem yapmak, bu sistemde en olası kullanıcı
 *                   hatasıdır."* Bu yüzden seçici üst çubuğun ortasında değil,
 *                   **başında** durur ve dar ekranda da düşmez; menü düğmesi düşer,
 *                   scope düşmez.
 *                2) TEK KURUMU OLAN KULLANICI AÇILIR MENÜ GÖRMEZ (`scope-baglami.tsx`
 *                   notu 6), kurumun adını **düz metin** olarak görür. Seçilecek bir şey
 *                   yokken açılır menü göstermek, olmayan bir kararı varmış gibi
 *                   gösterirdi — ama bilgiyi gizlemek de olmaz: kullanıcı yine de
 *                   nerede olduğunu bilmelidir.
 *                3) *"TÜMÜ"* BİR SEÇENEKTİR, BOŞLUK DEĞİL. Kullanıcı bütün yetki
 *                   alanına birden bakabilmelidir; seçenek konmasaydı, üç yurda yetkili
 *                   bir müdür üç yurdun toplamını hiç göremezdi.
 *                4) YÜKLENİRKEN İSKELET GÖSTERİLİR, BOŞLUK DEĞİL. Gösterge bir an bile
 *                   kaybolmamalı (not 1); iskelet yerini tutar ve üst çubuk sıçramaz.
 *                5) SEÇİM FİLTREDİR, YETKİ DEĞİL. Gerekçesi `scope-baglami.tsx` notu
 *                   1'de; burada tekrar edilmiyor ama ekranın metni de bunu ima
 *                   etmemelidir — bu yüzden etiket *"Kurum"*dur, *"Yetki"* değil.
 * SON GÜNCELLEME: 2026-08-31 04:00:00
 */
import { Group, Select, Skeleton, Text } from '@mantine/core';
import { IconBuildingCommunity } from '@tabler/icons-react';
import type { ReactElement } from 'react';

import { metin } from '../metin';
import { useScope } from './scope-baglami';

/** `M1-01` alan politikası `name`i gizleyebilir; o zaman `code`, o da yoksa kimlik. */
function kurumAdi(ad: string | undefined, kod: string | undefined, id: string): string {
  return ad ?? kod ?? id;
}

export function ScopeSecici(): ReactElement | null {
  const { kurumlar, seciliId, sec, yukleniyor } = useScope();

  if (yukleniyor) {
    // Boş bırakılsaydı üst çubuk yüklenirken sıçrar ve scope göstergesi bir an
    // **yok** olurdu; `15` §5.3 onun gizlenmesini yasaklıyor. İskelet yerini tutuyor
    // (`15` §9 — bekleyen içerik iskeletle gösterilir, çarkla değil).
    return <Skeleton height={32} width={260} radius="sm" />;
  }

  // Başlıktaki not 2 — seçilecek bir şey yoksa düz metin.
  if (kurumlar.length <= 1) {
    const tek = kurumlar[0];
    return (
      <Group gap="xs" wrap="nowrap">
        <IconBuildingCommunity size={18} />
        <Text size="sm" fw={500} lineClamp={1}>
          {tek === undefined
            ? metin('scope.tumu')
            : kurumAdi(tek.name, tek.code, tek.id)}
        </Text>
      </Group>
    );
  }

  return (
    <Select
      // Görsel bir etiket üst çubuğu şişirirdi; ekran okuyucu için etiket yine de
      // zorunludur (`15` §13 — *"her alan etiketli"*).
      aria-label={metin('scope.etiket')}
      leftSection={<IconBuildingCommunity size={18} />}
      data={[
        // Başlıktaki not 3.
        { value: '', label: metin('scope.tumu') },
        ...kurumlar.map((kurum) => ({
          value: kurum.id,
          label: kurumAdi(kurum.name, kurum.code, kurum.id),
        })),
      ]}
      value={seciliId ?? ''}
      onChange={(deger) => sec(deger === null || deger === '' ? null : deger)}
      allowDeselect={false}
      checkIconPosition="right"
      w={260}
      size="sm"
    />
  );
}
